import axios from 'axios';
import { CONFIG } from 'src/config-global';
import EncryptUtils from 'src/utils/EncryptUtils';

/**
 * Fetches image blob from FileContentId and BlobName
 */
async function fetchImageBlob(fileContentId: number, blobName: string): Promise<Blob> {
  const body = {
    tk: EncryptUtils.encryptedData({ FileContentID: fileContentId, blobName }),
  };
  const res = await axios.post(`${CONFIG.baseUrl}/property/get-images`, body, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

/**
 * Converts image value (URL string or JSON with FileContentId/BlobName) to a blob URL
 * @param value - Can be a URL string or JSON string with {FileContentId, BlobName}
 * @returns Promise<string> - Blob URL or original URL
 */
export async function getImageUrl(value: string | null | undefined): Promise<string | null> {
  if (!value) return null;

  // If it's already a URL (starts with http or /), return it
  if (value.startsWith('http') || value.startsWith('/')) {
    return value;
  }

  // Otherwise, try to parse as JSON (FileContentId and BlobName)
  try {
    const imageData = JSON.parse(value);
    if (imageData.FileContentId && imageData.BlobName) {
      try {
        const blob = await fetchImageBlob(imageData.FileContentId, imageData.BlobName);
        return URL.createObjectURL(blob);
      } catch (err) {
        console.error('Error loading image from blob:', err);
        return null;
      }
    }
  } catch {
    // If not JSON, treat as URL
    return value;
  }

  return null;
}

/**
 * Gets image URLs for all background image fields
 * @param bgImages - Object with image values (URLs or JSON strings)
 * @returns Promise<object> - Object with blob URLs
 */
export async function getBackgroundImageUrls(bgImages: Record<string, string | null | undefined>): Promise<Record<string, string | null>> {
  const entries = Object.entries(bgImages);
  const promises = entries.map(([key, value]) => 
    getImageUrl(value).then(url => [key, url] as [string, string | null])
  );
  
  const results = await Promise.all(promises);
  
  return Object.fromEntries(results);
}

